
import React from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

type Experience = {
  company: string;
  period: string;
  title: string;
  description: string;
  highlights: string[];
  logo?: string;
};

const ExperienceSection = () => {
  const experiences: Experience[] = [
    {
      company: "Marion Hanna State Farm",
      period: "2024–Present",
      title: "Office Receptionist & Client Liaison",
      description: "Serving as the first point of contact for clients and managing essential office operations to support sales and service teams.",
      highlights: [
        "Manages client records and documentation with attention to detail",
        "Supports sales team through appointment scheduling and client follow-up",
        "Implements workflow improvements for greater efficiency",
        "Ensures positive client experience through professional communication"
      ],
      logo: "/lovable-uploads/a0b0a32c-7bef-4443-9ada-296b0604f190.png"
    },
    {
      company: "Boys & Girls Club – Columbia, SC",
      period: "August 2021 – May 2023",
      title: "Youth Counselor",
      description: "Supervised and mentored youth in after-school programs while planning and facilitating educational and recreational activities.",
      highlights: [
        "Supervised and mentored youth aged 6–12 in after-school programs, ensuring a safe and engaging environment",
        "Planned and facilitated educational and recreational activities, including arts and crafts, sports, and homework assistance",
        "Provided guidance and support to promote positive behavior and social development among club members",
        "Collaborated with staff and parents to address individual needs and foster a supportive community",
        "Assisted in organizing special events and field trips, enhancing the overall club experience for participants"
      ],
      logo: "/lovable-uploads/dd10dfa9-20b1-4ef2-9d16-5d3d4710497c.png"
    },
    {
      company: "Francis Marion University – ABA Lab",
      period: "2020–2023",
      title: "Research Assistant",
      description: "Supported behavioral research initiatives while maintaining high standards of accuracy and scientific integrity.",
      highlights: [
        "Conducted trial logistics for behavioral experiments",
        "Maintained data integrity throughout research processes",
        "Collaborated with faculty and graduate students on research projects",
        "Applied psychological principles in research settings"
      ],
      logo: "/lovable-uploads/28b43b7f-cf6a-461c-80eb-db9eb3b42063.png"
    },
    {
      company: "Northeast Family Dentistry",
      period: "Summers",
      title: "Dental Assistant",
      description: "Balanced front desk responsibilities with chairside assistance to ensure smooth office operations.",
      highlights: [
        "Managed front desk operations including scheduling and records",
        "Assisted in patient care with compassion and professionalism",
        "Ensured compliance with healthcare regulations",
        "Improved patient experience through attentive communication"
      ],
      logo: "/lovable-uploads/cbaddb8e-eb88-4462-b8f9-2e579c7bc3d3.png"
    }
  ];
  
  return (
    <section id="experience" className="py-16 px-6">
      <div className="container max-w-5xl mx-auto">
        <div className="mb-12 text-center">
          <h2 className="text-sage-800 mb-2">Professional Experience</h2>
          <div className="w-24 h-1 bg-sage-300 mx-auto"></div>
        </div>
        
        <div className="space-y-10">
          {experiences.map((exp, index) => (
            <Card key={index} className="border border-sage-100 shadow-md hover:shadow-lg transition-all duration-300">
              <CardContent className="p-4 sm:p-6 md:p-8 relative">
                {/* Period Badge in the upper right */}
                <div className="absolute top-4 sm:top-6 right-4 sm:right-6 md:right-8">
                  <Badge variant="outline" className="bg-sage-50 text-sage-700 border-sage-200 font-medium">
                    {exp.period}
                  </Badge>
                </div>
                
                <div className="flex flex-col justify-between mb-4">
                  <div>
                    <h3 className="text-xl md:text-2xl font-serif text-sage-800">{exp.company}</h3>
                    <p className="text-sage-600 font-medium">{exp.title}</p>
                  </div>
                </div>
                
                {/* Mobile optimization - repositioned logo layout */}
                <div className="relative">
                  {/* On mobile, display logo above content */}
                  {exp.logo && (
                    <div className="md:hidden flex justify-center mb-4">
                      <div className="w-24 h-24 flex items-center justify-center">
                        <img 
                          src={exp.logo} 
                          alt={`${exp.company} logo`}
                          className="max-w-full max-h-full object-contain company-logo"
                        />
                      </div>
                    </div>
                  )}
                  
                  {/* Content with right padding only on desktop */}
                  <div className={`${exp.logo ? 'md:pr-[140px]' : ''}`}>
                    <p className="text-gray-700 mb-4">{exp.description}</p>
                    
                    <div className="mt-6">
                      <h4 className="text-sm font-medium text-sage-700 mb-3">Key Contributions:</h4>
                      <ul className="space-y-2">
                        {exp.highlights.map((item, idx) => (
                          <li key={idx} className="flex items-start">
                            <span className="mr-2 mt-1.5 h-1.5 w-1.5 rounded-full bg-sage-400 flex-shrink-0"></span>
                            <span className="text-gray-700">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                  
                  {/* Desktop logo position */}
                  {exp.logo && (
                    <div className="hidden md:block absolute bottom-0 right-0 group">
                      <div className="w-32 h-32 flex items-center justify-center transition-all duration-300 group-hover:scale-125 group-hover:z-10">
                        <img 
                          src={exp.logo} 
                          alt={`${exp.company} logo`} 
                          className="max-w-full max-h-full object-contain company-logo"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ExperienceSection;
